import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Check, ChevronDown, Shield, User, Building2, Stethoscope, Hand, ClipboardList, Handshake, Heart, GraduationCap, Map, Users, FileSearch, Megaphone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import type { UserRole } from '@/types';

const roleIcons = {
  admin: Shield,
  coach: Stethoscope,
  customer: User,
  examiner: ClipboardList,
  manager: Building2,
  partner: Handshake,
  volunteer: Hand,
  instructor: GraduationCap,
  organizer: Map,
  provider: Heart,
  coordinator: Users,
  audit: FileSearch,
  marketer: Megaphone,
} as const;

// Map internal role names to URL route paths
const roleToRoute: Record<UserRole, string> = {
  admin: 'admin',
  coach: 'doctor',
  customer: 'customer',
  examiner: 'examiner',
  manager: 'manager',
  partner: 'partner',
  volunteer: 'volunteer',
  instructor: 'instructor',
  organizer: 'organizer',
  provider: 'provider',
  coordinator: 'coordinator',
  audit: 'audit',
  marketer: 'marketer',
};

// Map URL route paths back to internal role names
const routeToRole: Record<string, UserRole> = {
  admin: 'admin',
  doctor: 'coach',
  customer: 'customer',
  examiner: 'examiner',
  manager: 'manager',
  partner: 'partner',
  volunteer: 'volunteer',
  instructor: 'instructor',
  organizer: 'organizer',
  provider: 'provider',
  coordinator: 'coordinator',
  audit: 'audit',
  marketer: 'marketer',
};

export function RoleSwitcher() {
  const { userData, switchRole } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { t, dir } = useLanguage();

  // Get active roles from user data
  const activeRoles: UserRole[] = userData?.roles?.filter((r: any) => r.isActive).map((r: any) => r.role) || [];
  
  // Add coach role for admin users if they don't have it explicitly
  const isAdmin = activeRoles.includes('admin');
  const hasCoachRole = activeRoles.includes('coach');
  const availableRoles: UserRole[] = isAdmin && !hasCoachRole ? [...activeRoles, 'coach' as UserRole] : activeRoles;
  
  // Get current active role from URL or use the first active role as fallback
  const pathSegment = window.location.pathname.split('/')[2];
  const pathRole = routeToRole[pathSegment] || pathSegment as UserRole;
  const currentRole = availableRoles.includes(pathRole) ? pathRole : availableRoles[0];

  const handleRoleSwitch = async (role: UserRole) => {
    await switchRole(role);
    navigate(`/dashboard/${roleToRoute[role]}`);
    setIsOpen(false);
  };

  if (availableRoles.length <= 1) {
    return null;
  }

  const Icon = currentRole && roleIcons[currentRole as keyof typeof roleIcons] || User;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          role="combobox"
          aria-expanded={isOpen}
          aria-label={t('navigation.selectRole')}
          className={cn(
            "h-9 w-auto min-w-0 max-w-[120px] sm:max-w-none",
            dir === 'rtl' ? 'px-2 sm:px-4' : 'px-2 sm:px-3'
          )}
        >
          <Icon className={`h-4 w-4 shrink-0 ${dir === 'rtl' ? 'ml-1 sm:ml-3' : 'mr-1 sm:mr-2'}`} />
          <span className={cn(
            "truncate text-sm sm:text-base",
            dir === 'rtl' ? 'ml-1 sm:ml-2' : 'mr-1 sm:mr-2'
          )}>{t(`dashboard.admin.users.roles.${currentRole}`)}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50 mx-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[200px]">
        {availableRoles.map((role: UserRole) => {
          const RoleIcon = roleIcons[role];
          const isVirtualRole = isAdmin && role === 'coach' && !hasCoachRole;
          return (
            <DropdownMenuItem
              key={role}
              onClick={() => handleRoleSwitch(role)}
              className={cn(
                "cursor-pointer",
                role === currentRole && "bg-accent"
              )}
            >
              <RoleIcon className={`h-4 w-4 ${dir === 'rtl' ? 'ml-3' : 'mr-2'}`} />
              <span className={cn(
                "flex-1",
                dir === 'rtl' ? 'ml-2' : 'mr-2'
              )}>{t(`dashboard.admin.users.roles.${role}`)}</span>
              {isVirtualRole && (
                <span className="text-xs text-muted-foreground">(Admin Access)</span>
              )}
              {role === currentRole && (
                <Check className={`h-4 w-4 ${dir === 'rtl' ? 'mr-3' : 'ml-2'}`} />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}